// Admin auction-registrations store backing the RealtimeRegistrationsProvider
// (`./RealtimeRegistrationsProvider.tsx`). BL-1262.
//
// The registrations-shaped BINDING of the generic engine in
// `./entity-state-store.ts` — the sibling of `lot-state-store.ts`. It supplies
// the four per-stream details (`registrationId` as the fold key, the `sequence`
// ordering key, the `registration.delta` event, and the catch-up response's
// `registrations` collection); every line of fold / cursor / cold-seed / reconcile /
// connection logic is inherited, so a fix to the fold benefits all three streams and
// none can drift from the others.
//
// BL-1630 turned GAP DETECTION on here, and the diff was this file's `cursorOf` plus two
// type members. That is not a coincidence — BL-1628 put detection in the shared engine
// and keyed it off the PAYLOAD carrying a `sequence`, not off anything a binding
// declares, so a stream opts in by claiming a sequence server-side and naming its
// position `sequence` instead of `cursor`. BL-1629 predicted it; this item confirmed it
// on the last of the three streams.

import type { AdminRegistrationDelta } from '../../mappers/realtime/admin-auction-registrations';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import {
    EntityStateStore,
    DISCONNECTED_ENTITY_STATE,
    type EntityStateConnection,
    type NotifyScheduler,
} from './entity-state-store';

/**
 * Result of the admin registrations catch-up route
 * (`createRegistrationsCatchupHandler`): the minted obscured `topic`, the current
 * state of every changed `registrations`, and the max `sequence` in that set (the
 * client's next `since_sequence`).
 *
 * It reports its position under `sequence` rather than `cursor` because this stream's
 * ordering key is a dense per-auction counter claimed in commit order — which is exactly
 * the signal `EntityStateStore` reads to turn gap detection on (BL-1630).
 *
 * This declaration is the ONE the wire envelope uses: `RegistrationsCatchupResponseBody`
 * in `@auctogon/realtime` imports it rather than re-declaring the shape (ADR 027 —
 * Response Contract).
 */
export interface RegistrationStateCatchupResult {
    topic: string;
    registrations: AdminRegistrationDelta[];
    sequence: number;
}

/**
 * Fetcher seam: given the auction + the client's current position, hit the
 * authenticated admin registrations catch-up route. `sinceSequence === 0` is the
 * cold-seed (every registration). The app injects this via the provider.
 */
export type RegistrationStateCatchupFetcher = (
    auctionId: string,
    sinceSequence: number,
) => Promise<RegistrationStateCatchupResult>;

/** The registrations stream's binding of the engine's four per-stream details. */
const REGISTRATION_STATE_CONFIG = {
    keyOf: (delta: AdminRegistrationDelta) => delta.registrationId,
    cursorOf: (delta: AdminRegistrationDelta) => delta.sequence,
    event: 'registration.delta' as const,
    itemsOf: (result: RegistrationStateCatchupResult) => result.registrations,
    // One delta per frame — this stream was not batched by BUG-1517. If it ever
    // is, only this line changes.
    deltasOf: (payload: unknown) => [(payload as { state: AdminRegistrationDelta }).state],
};

/**
 * Whether the registrations stream is established. Consumers render a connection
 * banner from this — a registration COUNT can't stand in for it, since an auction
 * may legitimately have zero registrations.
 */
export type RegistrationStateConnection = EntityStateConnection;

/** Stable pre-connection value (ref-stable for `useSyncExternalStore`). */
export const DISCONNECTED_REGISTRATION_STATE: RegistrationStateConnection =
    DISCONNECTED_ENTITY_STATE;

/**
 * The admin registrations store for ONE auction. A thin registrations-shaped
 * facade over `EntityStateStore` — `getRegistrations()` is the domain name for
 * the engine's `getEntities()`; everything else is inherited unchanged.
 */
export class RegistrationStateStore extends EntityStateStore<
    AdminRegistrationDelta,
    RegistrationStateCatchupResult
> {
    constructor(
        subscriber: RealtimeSubscriber,
        fetcher: RegistrationStateCatchupFetcher,
        auctionId: string,
        /**
         * Override the coalesced-notify scheduler (BUG-1407). Tests inject a
         * manual pump; production takes the default short-timer window.
         */
        scheduleNotify?: NotifyScheduler,
    ) {
        super(subscriber, fetcher, auctionId, REGISTRATION_STATE_CONFIG, scheduleNotify);
    }

    /** The current folded registration map. Stable ref until a fold changes it. */
    getRegistrations(): ReadonlyMap<string, AdminRegistrationDelta> {
        return this.getEntities();
    }
}
